import React, { useState, useEffect } from 'react';
import ICAL from 'ical.js';
import './CalendarView.css';

const CalendarView = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarFeeds, setCalendarFeeds] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [fetchingEvents, setFetchingEvents] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayDetail, setShowDayDetail] = useState(false);

  // Load calendar feeds from localStorage
  useEffect(() => {
    // Initialize with 2 Outlook calendars only (Google removed for privacy)
    const initialFeeds = [
      {
        id: 1,
        name: 'Kingsbury Digital',
        url: 'https://outlook.office365.com/owa/calendar/d13377b7cd774110a86184dd538402cd@kingsburydigital.com/622b5238265b44cfad4f401f054baecc39053378733870743/calendar.ics',
        color: '#1e40af'
      },
      {
        id: 2,
        name: '1209 Digital',
        url: 'https://outlook.office365.com/owa/calendar/db1370c303b8445b8b17bd31bb73c87b@1209digital.com/89f9f90b13544856afb21cd0c967941214089436678730631924/calendar.ics',
        color: '#3b82f6'
      }
    ];
    setCalendarFeeds(initialFeeds);
    localStorage.setItem('calendarFeeds', JSON.stringify(initialFeeds));
    setLoading(false);
  }, []);

  // Fetch events from all calendar feeds
  useEffect(() => {
    if (calendarFeeds.length > 0) {
      fetchAllEvents();
    }
  }, [calendarFeeds, currentDate]);

  const fetchAllEvents = async () => {
    setFetchingEvents(true);
    const allEvents = [];

    console.log('Fetching events from', calendarFeeds.length, 'calendars');

    for (const feed of calendarFeeds) {
      try {
        console.log(`Fetching calendar: ${feed.name}`);
        const events = await fetchCalendarEvents(feed);
        console.log(`Got ${events.length} events from ${feed.name}`);
        allEvents.push(...events);
      } catch (error) {
        console.error(`Error fetching calendar ${feed.name}:`, error);
      }
    }

    console.log('Total events loaded:', allEvents.length);
    setEvents(allEvents);
    setFetchingEvents(false);
  };

  const fetchCalendarEvents = async (feed) => {
    try {
      // Use our own API Gateway proxy to fetch iCal data (avoids CORS issues)
      const apiUrl = process.env.REACT_APP_API_URL || 'https://8q6l2ed5m6.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/calendar-proxy?url=${encodeURIComponent(feed.url)}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const icalData = await response.text();

      // Parse iCal data
      const jcalData = ICAL.parse(icalData);
      const comp = new ICAL.Component(jcalData);
      const vevents = comp.getAllSubcomponents('vevent');

      const events = [];

      // Calculate date range for expanding recurring events (1 year from now)
      const now = new Date();
      const oneYearFromNow = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);

      vevents.forEach(vevent => {
        try {
          const event = new ICAL.Event(vevent);

          // Check if this is a recurring event
          if (event.isRecurring()) {
            // Expand recurring events within the date range
            const expand = event.iterator();
            let next;
            let occurrenceCount = 0;
            const maxOccurrences = 500; // Safety limit

            while ((next = expand.next()) && occurrenceCount < maxOccurrences) {
              const occurrenceDate = next.toJSDate();

              // Stop if we've gone past one year
              if (occurrenceDate > oneYearFromNow) break;

              // Calculate end time
              const duration = event.duration;
              const endDate = new Date(occurrenceDate);
              if (duration) {
                endDate.setSeconds(endDate.getSeconds() + duration.toSeconds());
              } else {
                // Default to 1 hour if no duration
                endDate.setHours(endDate.getHours() + 1);
              }

              events.push({
                id: `${event.uid}-${occurrenceDate.getTime()}`,
                title: event.summary,
                start: occurrenceDate,
                end: endDate,
                description: event.description,
                location: event.location,
                calendar: feed.name,
                color: feed.color
              });

              occurrenceCount++;
            }
          } else {
            // Non-recurring event
            events.push({
              id: event.uid,
              title: event.summary,
              start: event.startDate.toJSDate(),
              end: event.endDate.toJSDate(),
              description: event.description,
              location: event.location,
              calendar: feed.name,
              color: feed.color
            });
          }
        } catch (eventError) {
          console.warn('Error parsing individual event:', eventError);
        }
      });

      return events;
    } catch (error) {
      console.error(`Error parsing calendar ${feed.name}:`, error);
      return [];
    }
  };

  const getEventsForDay = (year, month, day) => {
    const dayStart = new Date(year, month, day, 0, 0, 0);
    const dayEnd = new Date(year, month, day, 23, 59, 59);

    return events.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);

      // Check if event overlaps with this day
      return (eventStart <= dayEnd && eventEnd >= dayStart);
    });
  };

  const addCalendarFeed = (name, url, color) => {
    const newFeed = {
      id: Date.now(),
      name,
      url,
      color
    };
    const updated = [...calendarFeeds, newFeed];
    setCalendarFeeds(updated);
    localStorage.setItem('calendarFeeds', JSON.stringify(updated));
  };

  const removeCalendarFeed = (id) => {
    const updated = calendarFeeds.filter(f => f.id !== id);
    setCalendarFeeds(updated);
    localStorage.setItem('calendarFeeds', JSON.stringify(updated));
  };

  // Get days in current month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = (year, month, day) => {
    setSelectedDay({ year, month, day });
    setShowDayDetail(true);
  };

  const closeDayDetail = () => {
    setShowDayDetail(false);
    setSelectedDay(null);
  };

  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 7; hour <= 20; hour++) {
      slots.push({
        hour,
        label: `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`
      });
    }
    return slots;
  };

  const isTimeSlotFree = (hour, dayEvents) => {
    return !dayEvents.some(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      const slotStart = hour;
      const slotEnd = hour + 1;

      const eventStartHour = eventStart.getHours() + (eventStart.getMinutes() / 60);
      const eventEndHour = eventEnd.getHours() + (eventEnd.getMinutes() / 60);

      return (eventStartHour < slotEnd && eventEndHour > slotStart);
    });
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Create calendar grid
  const calendarDays = [];

  // Add empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
  }

  // Add days of the month
  const today = new Date();
  const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;

  for (let day = 1; day <= daysInMonth; day++) {
    const isToday = isCurrentMonth && today.getDate() === day;
    const dayEvents = getEventsForDay(year, month, day);

    calendarDays.push(
      <div
        key={day}
        className={`calendar-day ${isToday ? 'today' : ''} ${dayEvents.length > 0 ? 'has-events' : ''}`}
        onClick={() => handleDayClick(year, month, day)}
      >
        <div className="day-number">{day}</div>
        <div className="day-events">
          {dayEvents.slice(0, 3).map((event, idx) => (
            <div
              key={idx}
              className="event-dot"
              style={{ background: event.color }}
              title={`${event.title} - ${event.calendar}`}
            >
              <span className="event-title">{event.title}</span>
            </div>
          ))}
          {dayEvents.length > 3 && (
            <div className="more-events">+{dayEvents.length - 3} more</div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="calendar-loading">Loading calendar...</div>;
  }

  return (
    <div className="calendar-view">
      <div className="calendar-header">
        <div className="calendar-nav">
          <button onClick={() => changeMonth(-1)} className="btn-nav">‹</button>
          <h3>{monthNames[month]} {year}</h3>
          <button onClick={() => changeMonth(1)} className="btn-nav">›</button>
        </div>
        <div className="calendar-actions">
          <button onClick={goToToday} className="btn-today">Today</button>
          <button onClick={() => setShowSettings(!showSettings)} className="btn-settings">
            ⚙️ Calendars
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="calendar-settings">
          <h4>Connected Calendars</h4>
          <div className="calendar-feeds-list">
            {calendarFeeds.map(feed => (
              <div key={feed.id} className="calendar-feed-item">
                <div className="feed-info">
                  <span className="feed-color-dot" style={{ background: feed.color }}></span>
                  <span className="feed-name">{feed.name}</span>
                </div>
                <button
                  onClick={() => removeCalendarFeed(feed.id)}
                  className="btn-remove"
                  title="Remove calendar"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <div className="add-calendar-info">
            <p><strong>To add more calendars:</strong></p>
            <p>Get the iCal (.ics) link from Google Calendar, Outlook, or iCloud and contact support.</p>
          </div>
        </div>
      )}

      <div className="calendar-grid">
        <div className="calendar-weekdays">
          {dayNames.map(day => (
            <div key={day} className="weekday-name">{day}</div>
          ))}
        </div>
        <div className="calendar-days">
          {calendarDays}
        </div>
      </div>

      <div className="calendar-note">
        <p>📅 Displaying {calendarFeeds.length} calendar(s) • {events.length} event(s) loaded</p>
        {fetchingEvents && <p className="note-detail">Syncing calendar events...</p>}
      </div>

      {/* Day Detail Modal */}
      {showDayDetail && selectedDay && (
        <div className="day-detail-overlay" onClick={closeDayDetail}>
          <div className="day-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="day-detail-header">
              <h3>
                {monthNames[selectedDay.month]} {selectedDay.day}, {selectedDay.year}
              </h3>
              <button className="btn-close-modal" onClick={closeDayDetail}>✕</button>
            </div>

            <div className="day-detail-body">
              {(() => {
                const dayEvents = getEventsForDay(selectedDay.year, selectedDay.month, selectedDay.day);
                const timeSlots = getTimeSlots();

                return (
                  <div className="time-schedule">
                    {timeSlots.map(slot => {
                      const isFree = isTimeSlotFree(slot.hour, dayEvents);
                      const slotEvents = dayEvents.filter(event => {
                        const eventStart = new Date(event.start);
                        const eventStartHour = eventStart.getHours();
                        return eventStartHour === slot.hour;
                      });

                      return (
                        <div key={slot.hour} className={`time-slot ${isFree ? 'free' : 'busy'}`}>
                          <div className="time-label">{slot.label}</div>
                          <div className="time-content">
                            {isFree ? (
                              <div className="free-slot">
                                <span className="free-indicator">✓ Available</span>
                              </div>
                            ) : (
                              <div className="events-in-slot">
                                {slotEvents.map((event, idx) => {
                                  const start = new Date(event.start);
                                  const end = new Date(event.end);
                                  const startTime = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                                  const endTime = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

                                  return (
                                    <div
                                      key={idx}
                                      className="slot-event"
                                      style={{ borderLeft: `4px solid ${event.color}` }}
                                    >
                                      <div className="event-time">{startTime} - {endTime}</div>
                                      <div className="event-title-full">{event.title}</div>
                                      <div className="event-calendar">{event.calendar}</div>
                                      {event.location && (
                                        <div className="event-location">📍 {event.location}</div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div className="day-detail-footer">
              <div className="day-summary">
                {(() => {
                  const dayEvents = getEventsForDay(selectedDay.year, selectedDay.month, selectedDay.day);
                  const timeSlots = getTimeSlots();
                  const freeSlots = timeSlots.filter(slot => isTimeSlotFree(slot.hour, dayEvents));

                  return (
                    <>
                      <span className="summary-item">
                        <strong>{dayEvents.length}</strong> events
                      </span>
                      <span className="summary-item">
                        <strong>{freeSlots.length}</strong> free hours
                      </span>
                    </>
                  );
                })()}
              </div>
              <button className="btn-close" onClick={closeDayDetail}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
